import React, { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { ButtonsCollection, FormsCollection, ConfirmModal } from '@/ui-kit';
import api from '@/api/activity.service';
import { callNotification } from '@/utils/helpers/notifies';
import { userSelectors } from '@/state/user';
import './style-work-schedule.sass';
import {
  AVANGA_SAVED_ENTRIES_KEY,
  SCHEDULE_TYPES,
  WORK_PLACES,
  getScheduleTypeByValue,
  getWhereByValue,
} from '@/constants/dailyLogs';

const uid = () => {
  return Math.random().toString(16).slice(2) + Date.now().toString(16);
};

const toMinutes = (hhmm) => {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
  if (!m) return 0;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h * 60 + min;
};

const minutesToHours = (mins) => {
  return Math.max(0, mins) / 60;
};

const calcDurationMinutes = (start, end) => {
  const startMinutes = toMinutes(start);
  const endMinutes = toMinutes(end);
  return Math.max(0, endMinutes - startMinutes);
};

const fmtHours = (hours) => {
  const rounded = Math.round(hours * 100) / 100;
  const asStr = rounded.toFixed(2);
  return asStr.endsWith('.00') ? String(rounded.toFixed(0)) : asStr.replace(/0$/, '');
};

function clampTimeValue(value) {
  if (!value) return '';
  const m = /^([0-9]{1,2}):([0-9]{1,2})$/.exec(value);
  if (!m) return value;
  const h = Math.min(23, Math.max(0, Number(m[1])));
  const mm = Math.min(59, Math.max(0, Number(m[2])));
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

const minsToHHMM = (mins) => {
  const clamped = Math.min(mins, 23 * 60 + 59);
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const WorkScheduleSubmit = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const paramUserId = queryParams.get('userId');
  const paramDate = queryParams.get('date');
  const history = useHistory();
  const currentUser = useSelector(userSelectors.getUserData);

  const today = moment().format('YYYY-MM-DD');
  const isReadOnly =
    (paramUserId !== null && paramUserId !== String(currentUser?.id)) ||
    (paramDate !== null && paramDate !== today);

  const [isSaved, setIsSaved] = useState(isReadOnly);
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState('');
  const [userName, setUserName] = useState('');
  const [templates, setTemplates] = useState([]);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templateLabel, setTemplateLabel] = useState('');
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);

  useEffect(() => {
    const fetchUserDailyLog = async () => {
      try {
        const response = await api.getDailyLogsByUser({
          userId: paramUserId ?? undefined,
          date: paramDate ?? undefined,
        });

        if (response.length > 0) {
          const log = response[0];
          setRows(log.entries.map((entry) => ({ ...entry, id: entry.id })));
          setNotes(log.notes ?? '');
          setIsSaved(true);
          if (paramUserId && log.first_name) {
            setUserName(`${log.first_name} ${log.last_name}`);
          }
        } else if (!isReadOnly) {
          const savedEntries = localStorage.getItem(AVANGA_SAVED_ENTRIES_KEY);

          if (savedEntries) {
            const parsed = JSON.parse(savedEntries);
            setRows(parsed.entries.map((entry) => ({ ...entry, id: uid() })));
            setNotes(parsed.notes ?? null);
            callNotification.success('Daily log filled in from last entry');
          } else {
            setRows([
              {
                id: uid(),
                start_time: '09:00',
                end_time: '13:00',
                schedule_type: 'WORK',
                work_place: 'HOME',
              },
              {
                id: uid(),
                start_time: '14:00',
                end_time: '18:00',
                schedule_type: 'WORK',
                work_place: 'HOME',
              },
            ]);
          }
        }
      } catch (error) {
        console.error('Error fetching user daily log:', error);
      }
    };

    fetchUserDailyLog();
  }, [paramDate]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await api.getDailyLogTemplates();
        if (Array.isArray(response)) {
          setTemplates(response);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };

    fetchTemplates();
  }, []);

  const applyTemplate = (templateEntries) => {
    setRows(templateEntries.map((entry) => ({ ...entry, id: uid() })));
    callNotification.success('Template applied');
  };

  const handleCreateTemplate = async () => {
    if (!templateLabel.trim()) {
      callNotification.error('Please enter a template label');
      return;
    }

    setIsCreatingTemplate(true);

    const body = {
      label: templateLabel.trim(),
      entries: rows.map((row) => {
        const { id: _id, ...data } = row;
        return data;
      }),
    };

    try {
      await api.submitDailyLogTemplate(body);
      callNotification.success('Template created successfully');
      setIsTemplateModalOpen(false);
      setTemplateLabel('');

      const response = await api.getDailyLogTemplates();
      if (Array.isArray(response)) {
        setTemplates(response);
      }
    } catch (error) {
      callNotification.error(error.message || 'Failed to create template');
    } finally {
      setIsCreatingTemplate(false);
    }
  };

  const submitDailyLog = async () => {
    const strippedNotes = (notes ?? '').replace(/<[^>]*>/g, '').trim();

    const body = {
      entries: rows.map((row) => {
        const { id: _id, ...data } = row;

        return data;
      }),
      notes: strippedNotes.length === 0 ? null : strippedNotes,
    };

    await api
      .submitDailyLog(body)
      .then(() => {
        localStorage.setItem(AVANGA_SAVED_ENTRIES_KEY, JSON.stringify(body));
        callNotification.success('Daily log submitted successfully');
        history.push('/activity/daily-logs/');
      })
      .catch((error) => {
        callNotification.error(error.message ?? 'Failed to submit daily logs');
      });
  };

  const summary = useMemo(() => {
    const totals = {
      WORK: 0,
      DAY_OFF: 0,
      SICK_LEAVE: 0,
    };

    for (const r of rows) {
      const mins = calcDurationMinutes(r.start_time, r.end_time);
      totals[r.schedule_type] += mins;
    }

    return {
      workedHours: minutesToHours(totals.WORK),
      timeoffHours: minutesToHours(totals.DAY_OFF),
      sickLeaveHours: minutesToHours(totals.SICK_LEAVE),
    };
  }, [rows]);

  const addRow = () => {
    setRows((prev) => {
      const prevRow = prev[prev.length - 1];
      // pick 9:00 as the default if no end time was set
      const newStartMins = prevRow?.end_time ? toMinutes(prevRow.end_time) + 60 : 60 * 9;
      const newEndMins = newStartMins + 60;

      return [
        ...prev,
        {
          id: uid(),
          start_time: minsToHHMM(newStartMins),
          end_time: minsToHHMM(newEndMins),
          schedule_type: 'WORK',
          work_place: 'OFFICE',
        },
      ];
    });
  };

  function removeRow(id) {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  function updateRow(id, patch) {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, ...patch };
        if (next.schedule_type !== 'WORK') {
          delete next.work_place;
        } else {
          next.work_place = next.work_place ?? 'OFFICE';
        }
        return next;
      }),
    );
  }

  const handleCancel = () => {
    if (paramDate) {
      history.push(`/activity/daily-logs/?date=${paramDate}`);
    } else {
      history.push('/activity/daily-logs/');
    }
  };

  return (
    <div className="work-schedule-submit">
      <div className="work-schedule-submit__container">
        <div className="work-schedule-submit__header">
          <h2 className="work-schedule-submit__title">
            {userName ? `Daily Log — ${userName} — ${paramDate || today}` : 'Submit Daily Log'}
          </h2>
          <div className="work-schedule-submit__actions">
            {!isSaved && !isReadOnly && (
              <ButtonsCollection.ButtonBrill
                onClick={() => addRow()}
                className="button-brill button--sm button-brill--fill work-schedule-submit__add-btn"
              >
                + Add row
              </ButtonsCollection.ButtonBrill>
            )}
            {isSaved ? (
              !isReadOnly && (
                <ButtonsCollection.ButtonBrill
                  onClick={() => setIsSaved(false)}
                  className="button--sm"
                >
                  Edit
                </ButtonsCollection.ButtonBrill>
              )
            ) : (
              <ButtonsCollection.ButtonBrill onClick={submitDailyLog} className="button--sm">
                Save
              </ButtonsCollection.ButtonBrill>
            )}
            <ButtonsCollection.ButtonBrill
              onClick={handleCancel}
              className="button--secondary button-brill button--sm button-brill--fill"
            >
              Cancel
            </ButtonsCollection.ButtonBrill>
          </div>
        </div>

        {!isReadOnly && !isSaved && (
          <div className="work-schedule-submit__templates">
            {templates.map((template) => (
              <ButtonsCollection.ButtonBrill
                key={template.id}
                onClick={() => applyTemplate(template.entries)}
                className="button--secondary button-brill button--sm button-brill--fill"
                disabled={isSaved}
              >
                {template.label}
              </ButtonsCollection.ButtonBrill>
            ))}
            <ButtonsCollection.ButtonBrill
              onClick={() => setIsTemplateModalOpen(true)}
              className="button--sm"
              disabled={isSaved || rows.length === 0}
            >
              + Create Template
            </ButtonsCollection.ButtonBrill>
          </div>
        )}

        <div className="work-schedule-submit__table">
          <div className="work-schedule-submit__table-header">
            <div className="work-schedule-submit__col work-schedule-submit__col--start">
              Start at
            </div>
            <div className="work-schedule-submit__col work-schedule-submit__col--end">End at</div>
            <div className="work-schedule-submit__col work-schedule-submit__col--type">Type</div>
            <div className="work-schedule-submit__col work-schedule-submit__col--where">Where</div>
            <div className="work-schedule-submit__col work-schedule-submit__col--hours" />
            <div className="work-schedule-submit__col work-schedule-submit__col--actions">
              Actions
            </div>
          </div>

          <div className="work-schedule-submit__table-body">
            {rows.map((r) => (
              <div key={r.id} className="work-schedule-submit__row">
                <div
                  className="work-schedule-submit__col work-schedule-submit__col--start"
                  data-label="Start at"
                >
                  {isSaved ? (
                    <span className="work-schedule-submit__value">{r.start_time}</span>
                  ) : (
                    <FormsCollection.Input
                      type="time"
                      value={r.start_time}
                      onChange={(e) =>
                        updateRow(r.id, { start_time: clampTimeValue(e.target.value) })
                      }
                      className="work-schedule-submit__input"
                    />
                  )}
                </div>

                <div
                  className="work-schedule-submit__col work-schedule-submit__col--end"
                  data-label="End at"
                >
                  {isSaved ? (
                    <span className="work-schedule-submit__value">{r.end_time}</span>
                  ) : (
                    <FormsCollection.Input
                      type="time"
                      value={r.end_time}
                      onChange={(e) =>
                        updateRow(r.id, { end_time: clampTimeValue(e.target.value) })
                      }
                      className="work-schedule-submit__input"
                    />
                  )}
                </div>

                <div
                  className="work-schedule-submit__col work-schedule-submit__col--type"
                  data-label="Type"
                >
                  {isSaved ? (
                    <span className="work-schedule-submit__value">
                      {getScheduleTypeByValue(r.schedule_type).label}
                    </span>
                  ) : (
                    <FormsCollection.Select
                      data={SCHEDULE_TYPES}
                      value={r.schedule_type}
                      onChange={({ target: { value } }) =>
                        updateRow(r.id, { schedule_type: value })
                      }
                      className="work-schedule-submit__select"
                    />
                  )}
                </div>

                <div
                  className="work-schedule-submit__col work-schedule-submit__col--where"
                  data-label="Where"
                >
                  {isSaved ? (
                    <span className="work-schedule-submit__value">
                      {r.schedule_type === 'WORK' ? getWhereByValue(r.work_place).label : '—'}
                    </span>
                  ) : (
                    <div
                      className={`work-schedule-submit__where-wrap${
                        r.schedule_type !== 'WORK' ? ' work-schedule-submit__disabled' : ''
                      }`}
                    >
                      <FormsCollection.Select
                        data={WORK_PLACES}
                        value={r.work_place ?? 'WFH'}
                        name="where"
                        onChange={(e) => updateRow(r.id, { work_place: e.target.value })}
                        disabled={r.schedule_type !== 'WORK'}
                        className="work-schedule-submit__select"
                      />
                    </div>
                  )}
                </div>

                <div
                  className="work-schedule-submit__col work-schedule-submit__col--hours"
                  data-label="Duration"
                >
                  <span className="work-schedule-submit__duration">
                    {fmtHours(minutesToHours(calcDurationMinutes(r.start_time, r.end_time)))}h
                  </span>
                </div>

                <div className="work-schedule-submit__col work-schedule-submit__col--actions">
                  {!isSaved && (
                    <ButtonsCollection.Button
                      onClick={() => removeRow(r.id)}
                      className="button--secondary work-schedule-submit__delete-btn"
                      disabled={rows.length <= 1}
                    >
                      ×
                    </ButtonsCollection.Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="work-schedule-submit__table-footnote">
            Note: time entries are automatically sorted by start time
          </div>
        </div>

        <div className="work-schedule-submit__summary">
          <div className="work-schedule-submit__summary-item">
            <div className="work-schedule-submit__summary-label">Worked hours</div>
            <div className="work-schedule-submit__summary-value">
              {fmtHours(summary.workedHours)} {'hours'}
            </div>
          </div>
          <div className="work-schedule-submit__summary-item">
            <div className="work-schedule-submit__summary-label">Day-off hours</div>
            <div className="work-schedule-submit__summary-value">
              {fmtHours(summary.timeoffHours)} {'hours'}
            </div>
          </div>
          <div className="work-schedule-submit__summary-item">
            <div className="work-schedule-submit__summary-label">Sick leave hours</div>
            <div className="work-schedule-submit__summary-value">
              {fmtHours(summary.sickLeaveHours)} {'hours'}
            </div>
          </div>
        </div>

        <div className="work-schedule-submit__notes">
          <span className="work-schedule-submit__notes-label">Your standup/notes</span>
          {isSaved ? (
            <div className="work-schedule-submit__notes-display">{notes?.trim() ? notes : '—'}</div>
          ) : (
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What did you do? Any blockers? Plans for today…"
              className="work-schedule-submit__notes-textarea"
            />
          )}
        </div>

        <div className="work-schedule-submit__footer">
          {isSaved ? (
            !isReadOnly && (
              <ButtonsCollection.ButtonBrill
                onClick={() => setIsSaved(false)}
                className="button--sm"
              >
                Edit
              </ButtonsCollection.ButtonBrill>
            )
          ) : (
            <ButtonsCollection.ButtonBrill onClick={submitDailyLog} className="button--sm">
              Save
            </ButtonsCollection.ButtonBrill>
          )}
        </div>
        <div className="work-schedule-submit__bottom-spacer" />
      </div>

      <ConfirmModal
        isOpen={isTemplateModalOpen}
        onClose={() => {
          setIsTemplateModalOpen(false);
          setTemplateLabel('');
        }}
        onAction={handleCreateTemplate}
        actionTitle="Save"
        title="Create Template"
        isPending={isCreatingTemplate}
        disabledAction={!templateLabel.trim()}
      >
        <div style={{ marginBottom: '16px' }}>
          <FormsCollection.Input
            type="text"
            value={templateLabel}
            onChange={(e) => setTemplateLabel(e.target.value)}
            placeholder="Enter template name..."
            label="Template Label"
          />
        </div>
      </ConfirmModal>
    </div>
  );
};
